import { type ClassName } from "@typeblind/widget";
import { clsx } from "clsx";
import { type ReactNode } from "react";
import * as styles from "./Avatar.module.less";
import { type AnyUser } from "./types.ts";

/**
 * The user avatar: the first letter of the name in a coloured circle. The
 * colour is derived from the name, except in the anonymized state, where a
 * neutral colour is used instead (so "Anonymize me" is visibly distinct).
 */
export function Avatar({
  user,
  size = "normal",
  anonymized = false,
  className,
}: {
  readonly user: AnyUser | null;
  readonly size?: "normal" | "medium" | "large";
  readonly anonymized?: boolean;
  readonly className?: ClassName;
}): ReactNode {
  const sizeClassName = {
    [styles.size_normal]: size === "normal",
    [styles.size_medium]: size === "medium",
    [styles.size_large]: size === "large",
  };

  const name = user?.name ?? "";
  const backgroundColor = anonymized
    ? "var(--secondary-l2)"
    : `hsl(${hueOf(name)} 52% 48%)`;

  return (
    <span
      className={clsx(styles.root, sizeClassName, styles.initial, className)}
      style={{ backgroundColor }}
      aria-label={name || "User"}
    >
      {initialOf(name)}
    </span>
  );
}

function initialOf(name: string): string {
  const first = [...name.trim()][0];
  return first ? first.toUpperCase() : "?";
}

// A deterministic hue (0–359) from the name, so each user gets a stable colour.
function hueOf(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % 360;
  }
  return hash;
}
